import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface SurveyConfirmationModalProps {
  isOpen: boolean;
  onSendSurvey: () => void;
  onDontSend: () => void;
  title: string;
  message: string;
  isLoading?: boolean;
  defaultToggleOn?: boolean; // Default toggle state when modal opens
}

const SurveyConfirmationModal: React.FC<SurveyConfirmationModalProps> = ({
  isOpen,
  onSendSurvey,
  onDontSend,
  title,
  message,
  isLoading = false,
  defaultToggleOn = false // Default to false if not provided
}) => {
  const [toggleOn, setToggleOn] = useState(defaultToggleOn);

  // Reset toggle to defaultToggleOn when modal opens
  useEffect(() => {
    if (isOpen) {
      setToggleOn(defaultToggleOn);
    }
  }, [isOpen, defaultToggleOn]);

  const handleSendSurvey = () => {
    onSendSurvey();
    setToggleOn(false); // Reset toggle for next time
  };

  const handleDontSend = () => {
    onDontSend();
    setToggleOn(false); // Reset toggle for next time
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}} static>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                      {title}
                    </Dialog.Title>
                  </div>
                </div>

                <div className="mt-2 ml-9">
                  <p className="text-sm text-gray-500">
                    {message}
                  </p>
                </div>

                {/* Toggle Switch: No = do not send, Yes = send survey */}
                <div className="mt-6 ml-9 flex items-center gap-3">
                  <span
                    className={`text-sm font-medium ${!toggleOn ? 'text-gray-900' : 'text-gray-400'}`}
                    id="survey-toggle-no"
                  >
                    No
                  </span>
                  <button
                    type="button"
                    onClick={() => setToggleOn(!toggleOn)}
                    disabled={isLoading}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      toggleOn ? 'bg-blue-600' : 'bg-gray-200'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    role="switch"
                    aria-checked={toggleOn}
                    aria-labelledby="survey-toggle-no survey-toggle-yes"
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        toggleOn ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span
                    className={`text-sm font-medium ${toggleOn ? 'text-gray-900' : 'text-gray-400'}`}
                    id="survey-toggle-yes"
                  >
                    Yes
                  </span>
                </div>

                {/* Action Button */}
                <div className="mt-6 flex justify-end">
                  {toggleOn ? (
                    <button
                      type="button"
                      onClick={handleSendSurvey}
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                    >
                      {isLoading ? 'Processing...' : 'Send Survey'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleDontSend}
                      disabled={isLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-700 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                    >
                      Don't Send Survey
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SurveyConfirmationModal;

